import {
  Component,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../../core/pure-functions/rx-helper';
import { CometChatSuperAdminService } from '../../../../../core/services/cometChat/cometChatSuperAdmin.service';
import { CometChatService } from '../../../../../core/services/cometChat/cometChat.service';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../core/services/dao/userDAO';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  MessageHeaderAction,
  MessagingComponent,
} from '../../../../shared/messaging/messaging.component';
import { UserSearchDialogService } from '../../../../shared/dialogs/user-search-dialog/user-search-dialog-service';
import { UserSearchResult } from 'anchor-shared/lib/model/interface/userSearchResult';
import { CometChatDAO } from '../../../../../core/services/dao/cometChatDAO';
import {
  isCharterGroup,
  isUserInGroup,
} from '../../../../../core/services/cometChat/cometChatUtils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CometChatGroup } from 'anchor-shared/lib/model/interface/cometChatGroup';
import { generateNumericGUID } from 'anchor-shared/lib/service/utils/comet-chat-helper';

@Component({
  selector: 'app-admin-context',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminContextComponent implements AfterViewInit, OnDestroy {
  selectedView: 'admin' | 'superAdmin' = 'superAdmin';
  cometChatServiceProvider;
  anchorUser: User;

  menuOptions: MessageHeaderAction[] = [
    {
      icon: 'chat',
      text: ({ isMember }) => `${isMember ? 'Go to' : 'Join'} conversation`,
      fnc: ({ group, isMember }) =>
        isMember
          ? this.navigateToConversation(group.getGuid())
          : this.joinConversation(group),
      show: () => this.selectedView === 'superAdmin',
    },
    {
      icon: 'explore',
      text: () => 'Manage charter',
      subtext: ({ group }) =>
        this.isCharterGroup(group.getGuid())
          ? `Charter Id: ${group.getGuid()}`
          : '',
      fnc: ({ group }) =>
        this.openToPage('/manage/manage-charter/' + group.getGuid()),
      show: ({ group }) => this.isCharterGroup(group.getGuid()),
    },
    {
      icon: 'person_add',
      text: () => 'Add user',
      fnc: ({ group }) => this.searchAndAddUserToGroup(group),
      show: () => true,
    },
    {
      icon: 'logout',
      text: () => 'Leave group',
      fnc: ({ group }) => this.leaveGroup(group),
      show: () => this.selectedView === 'admin',
    },
  ];

  @ViewChild(MessagingComponent, { static: false })
  messagingComponent: MessagingComponent;
  newConversationLoading: boolean = false;
  isCharterGroup = isCharterGroup;
  subs: Subscription[] = [];

  constructor(
    private cometChatService: CometChatService,
    private cometChatSuperAdminService: CometChatSuperAdminService,
    private userDAO: UserDAO,
    private userSearchDialogService: UserSearchDialogService,
    private cometChatDAO: CometChatDAO,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {
    this.anchorUser = this.userDAO.getCurrentUser();
  }

  async ngAfterViewInit() {
    await this.initContext(this.selectedView);
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  // CONVERSATION

  async selectView(type: 'admin' | 'superAdmin'): Promise<void> {
    if (this.selectedView === type) return;
    try {
      const currentUser =
        await this.cometChatServiceProvider.getCometChatUser();
      if (currentUser) {
        await this.cometChatServiceProvider.logout();
      }

      this.cometChatServiceProvider = null;
      this.selectedView = type;

      if (type === 'superAdmin') {
        await this.cometChatSuperAdminService.login();
        this.cometChatServiceProvider = this.cometChatSuperAdminService;
      } else {
        await this.cometChatService.login();
        this.cometChatServiceProvider = this.cometChatService;
      }
    } catch (err) {
      console.error('Failed to switch views', err);
    }
  }

  private async initContext(type: 'admin' | 'superAdmin'): Promise<void> {
    if (type === 'superAdmin') {
      await this.cometChatSuperAdminService.login();
      this.cometChatServiceProvider = this.cometChatSuperAdminService;
    } else {
      await this.cometChatService.login();
      this.cometChatServiceProvider = this.cometChatService;
    }
  }

  async navigateToConversation(guid: string): Promise<void> {
    await this.selectView('admin');
    this.router.navigate([], {
      queryParams: { search: guid },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  async joinConversation(group: CometChat.Group): Promise<void> {
    try {
      const standardUid = this.anchorUser.id?.toString();

      if (!standardUid) {
        return;
      }

      await this.addUserToGroup(standardUid, group);
      this.snackBar.open('You have joined the group.', null, {
        duration: 4000,
        verticalPosition: 'top',
      });
    } catch (error) {
      console.error('Failed to join conversation as standard user:', error);
    }
  }

  async newConversation(): Promise<void> {
    const user: UserSearchResult = await this.searchAnchorUsers();

    if (user) {
      this.newConversationLoading = true;
      this.cometChatDAO
        .createGroup(`conv_${generateNumericGUID()}`, {
          admin: {
            name: this.anchorUser.firstName.split(' ')[0],
            id: this.anchorUser.id,
          },
          other: [{ name: user.firstName.split(' ')[0], id: user.id }],
        })
        .subscribe(async (group: CometChatGroup) => {
          if (this.messagingComponent) {
            await this.navigateToConversation(group.guid);
          } else {
            console.warn('conversationsComponent is not initialized yet.');
          }
          this.newConversationLoading = false;
        });
    }
  }

  // OPTIONS

  async leaveGroup(group: CometChat.Group): Promise<void> {
    try {
      const standardUid = this.anchorUser.id?.toString();
      this.cometChatDAO
        .removeUserFromGroup(group.getGuid(), standardUid)
        .subscribe(async () => {
          this.messagingComponent.setCurrUser(null);
          this.messagingComponent.conversationsComponent.initAllMessaging();
          this.snackBar.open('You have left the group.', null, {
            duration: 4000,
            verticalPosition: 'top',
          });
        });
    } catch (error) {
      console.error('Failed to leave group:', error);
    }
  }

  async searchAndAddUserToGroup(group): Promise<void> {
    const user: UserSearchResult = await this.searchAnchorUsers();
    if (user) {
      this.cometChatDAO
        .ensureCometChatUsersExist(user.id)
        .subscribe(async ({ success }) => {
          if (success) {
            await this.addUserToGroup(`${user.id}`, group);
          }
        });
    }
  }

  openToPage(url): void {
    window.open(url, '_blank');
  }

  // UTILITY

  searchAnchorUsers(): Promise<UserSearchResult | null> {
    return new Promise((resolve) => {
      const searchFxn = (searchText): Observable<UserSearchResult[]> => {
        return this.userDAO.adminUserSearch(searchText);
      };

      const sub = this.userSearchDialogService
        .open({ searchFxn })
        .subscribe((userSelection) => {
          if (userSelection) {
            resolve(userSelection);
          } else {
            resolve(null);
          }
        });

      this.subs.push(sub);
    });
  }

  addUserToGroup(uid: string, group: CometChat.Group): Observable<void> {
    const subject = new Subject<void>();

    isUserInGroup(group.getGuid(), uid)
      .then((isAlreadyMember) => {
        if (isAlreadyMember) {
          this.snackBar.open('User is already a member of this group.', null, {
            duration: 4000,
            verticalPosition: 'top',
          });
          subject.complete();
        } else {
          this.cometChatDAO.addUserToGroup(group.getGuid(), uid).subscribe({
            next: () => {
              subject.next();
              subject.complete();
            },
            error: (err) => {
              subject.error(err);
            },
          });
        }
      })
      .catch((err) => {
        subject.error(err);
      });

    return subject.asObservable();
  }
}
